/**
 * The instance default for reminders to external delegates
 * (`global/workOrderReminders`) and, for Aviseringar, its admin save.
 * `enabled` gates the read (the composer reads it only while open).
 */
import { useCallback, useEffect, useState } from 'react';
import { getReminderDefaults, saveReminderDefaults } from '../../api/workOrderRemindersApi';
import { DEFAULT_REMINDER_TIMING, ReminderTiming } from '../../types/workOrderReminders';

export interface UseReminderDefaultsReturn {
    defaults: ReminderTiming;
    loading: boolean;
    saving: boolean;
    error: string | null;
    save: (timing: ReminderTiming) => Promise<void>;
}

export const useReminderDefaults = (enabled = true): UseReminderDefaultsReturn => {
    const [defaults, setDefaults] = useState<ReminderTiming>(DEFAULT_REMINDER_TIMING);
    const [fetching, setFetching] = useState(false);
    // True once a read has settled. Loading is derived from it, so it is true
    // in the very render that enables the hook, before the effect starts the
    // read; a caller never sees the built-in default as if it were loaded.
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setFetching(true);
        getReminderDefaults()
            .then(d => { if (!cancelled) setDefaults(d); })
            .catch(e => { console.error(e); if (!cancelled) setError('Kunde inte läsa påminnelserna'); })
            .finally(() => { if (!cancelled) { setFetching(false); setLoaded(true); } });
        return () => { cancelled = true; };
    }, [enabled]);

    const loading = enabled && (fetching || !loaded);

    const save = useCallback(async (timing: ReminderTiming) => {
        setSaving(true);
        setError(null);
        try {
            await saveReminderDefaults(timing);
            setDefaults(timing);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Kunde inte spara påminnelserna');
            throw e;
        } finally {
            setSaving(false);
        }
    }, []);

    return { defaults, loading, saving, error, save };
};
